// /reset-password#<token>: set a new password from an emailed Ops link (1.0's
// reset, POST /auth/reset-password, the same one league admins use). The
// token rides in the fragment, so it never reaches a server log or a
// Referer; it is read once, at first render, and cleared from the address
// bar at once (history.replaceState). There is no pre-check: a dead link
// (unknown, expired or used) is reported when the form is sent, as a 400
// with `reason: 'invalid_token'`. A reset never signs anyone in: the last
// step links to /login and nothing is stored.

import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { api } from '@/api'
import { isApiError } from '@/api/client'
import { Button, ButtonLink, FormField, InlineAlert, TextInput } from '@/components/ui'
import { useDocumentTitle } from '@/lib/hooks'
import { AuthCard } from './AuthCard'

const MIN_LENGTH = 8
const MAX_LENGTH = 200
const WAIT = 'Too many attempts. Please wait a minute and try again.'
const DEAD_LINK = 'This reset link has expired or was already used. Ask for a new one.'
const MISSING_TOKEN = 'This reset link is incomplete. Open it again from the email, or ask for a new one.'

type Phase = 'form' | 'dead' | 'done'

export default function ResetPasswordPage() {
  useDocumentTitle('Choose a new password')
  const location = useLocation()
  // Read once: the address bar is cleared right after, and a StrictMode
  // re-run must not read it again.
  const [token] = useState(() => location.hash.replace(/^#/, ''))
  const [phase, setPhase] = useState<Phase>('form')
  const [pw, setPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [pwErr, setPwErr] = useState<string | null>(null)
  const [confirmErr, setConfirmErr] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    // Take the token out of the address bar, keeping the router's history state.
    if (window.location.hash) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search)
  }, [])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    const p1 = pw.length < MIN_LENGTH ? 'Use at least 8 characters.' : pw.length > MAX_LENGTH ? 'Use at most 200 characters.' : null
    const p2 = !p1 && pw !== confirm ? 'The passwords don’t match.' : null
    setPwErr(p1)
    setConfirmErr(p2)
    if (p1 || p2) return
    setPending(true)
    try {
      await api.auth.resetPassword(token, pw)
      setPw('')
      setConfirm('')
      setPhase('done')
    } catch (err) {
      if (isApiError(err) && err.status === 400 && err.reason === 'invalid_token') setPhase('dead')
      else if (isApiError(err) && err.status === 400 && err.field === 'password') setPwErr(err.message)
      else if (isApiError(err) && err.status === 429) setError(WAIT)
      else setError(err instanceof Error && err.message ? err.message : 'Something went wrong. Try again.')
    } finally {
      setPending(false)
    }
  }

  let body: ReactNode
  if (phase === 'done')
    body = (
      <div className="grid gap-4">
        <div role="status" className="grid gap-2">
          <h1 className="text-2xl font-extrabold leading-tight text-ink">Your password has been reset</h1>
          <p className="text-[13px] text-muted">Sign in with your new password.</p>
        </div>
        <ButtonLink to="/login" variant="primary" block>
          Sign in
        </ButtonLink>
      </div>
    )
  else if (phase === 'dead' || !token)
    body = (
      <div className="grid gap-4">
        <h1 className="text-[1.75rem] font-extrabold leading-none text-ink">Choose a new password</h1>
        <InlineAlert tone="bad">{token ? DEAD_LINK : MISSING_TOKEN}</InlineAlert>
        <ButtonLink to="/forgot-password" variant="primary" block>
          Ask for a new link
        </ButtonLink>
      </div>
    )
  else
    body = (
      <form onSubmit={submit} noValidate className="grid gap-4">
        <h1 className="text-[1.75rem] font-extrabold leading-none text-ink">Choose a new password</h1>
        {error ? <InlineAlert tone="bad">{error}</InlineAlert> : null}
        <FormField label="New password" error={pwErr} hint="At least 8 characters.">
          {({ id, describedBy, invalid }) => (
            <TextInput id={id} aria-describedby={describedBy} invalid={invalid} type="password" autoComplete="new-password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} disabled={pending} />
          )}
        </FormField>
        <FormField label="Confirm new password" error={confirmErr}>
          {({ id, describedBy, invalid }) => (
            <TextInput id={id} aria-describedby={describedBy} invalid={invalid} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={pending} />
          )}
        </FormField>
        <Button type="submit" variant="primary" block loading={pending} className="h-10">
          Reset password
        </Button>
      </form>
    )

  return <AuthCard>{body}</AuthCard>
}
